// src/pages/components/ProjectDetailsNameCard.js
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Box, TextField, Typography } from '@mui/material';

const MAX_LEN = 100;

const ProjectDetailsNameCard = ({ project, projectId, onProjectUpdated }) => {
  const initialName = project?.name || 'Project Name';

  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState(initialName);
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const inputRef = useRef(null);

  useEffect(() => {
    if (!isEditingName) setDraftName(initialName);
  }, [initialName, isEditingName]);

  useEffect(() => {
    if (isEditingName) setTimeout(() => inputRef.current?.focus(), 0);
  }, [isEditingName]);

  const startEditing = () => {
    setNameError('');
    setSaveSuccess(false);
    setIsEditingName(true);
  };

  const cancelEditing = () => {
    setNameError('');
    setSaveSuccess(false);
    setDraftName(initialName);
    setIsEditingName(false);
  };

  const submitName = async () => {
    const next = (draftName || '').trim();
    setSaveSuccess(false);

    if (!next) {
      setNameError('Project name cannot be empty.');
      return;
    }

    if (next === (initialName || '').trim()) {
      setIsEditingName(false);
      setNameError('');
      return;
    }

    try {
      setSavingName(true);
      setNameError('');

      const res = await axios.patch(`/api/v1/project/${projectId}/details`, { name: next });
      const doc = res?.data?.data?.project || res?.data?.data?.doc || res?.data?.data || res?.data || null;

      setSaveSuccess(true);

      if (onProjectUpdated) {
        if (doc && typeof doc === 'object') onProjectUpdated(doc);
        else onProjectUpdated({ ...project, name: next, updatedAt: new Date().toISOString() });
      }

      setIsEditingName(false);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to rename project.';
      setNameError(msg);
    } finally {
      setSavingName(false);
    }
  };

  useEffect(() => {
    if (!saveSuccess) return;
    const t = setTimeout(() => setSaveSuccess(false), 2000);
    return () => clearTimeout(t);
  }, [saveSuccess]);

  const message = nameError ? nameError : saveSuccess ? 'Project name updated!' : '';
  const countValue = (isEditingName ? draftName : initialName).length;
  const counter = `${countValue}/${MAX_LEN}`;

  return (
    <Box className="project-details-project-name-container">
      {!isEditingName ? (
        <TextField
          fullWidth
          label="Project Name"
          value={initialName}
          onClick={startEditing}
          title="Click to rename"
          InputLabelProps={{
            sx: {
              fontWeight: 700,
            },
          }}
        />
      ) : (
        <TextField
          fullWidth
          label="Project Name"
          inputRef={inputRef}
          value={draftName}
          disabled={savingName}
          error={Boolean(nameError)}
          onChange={(e) => {
            const v = e.target.value;
            setDraftName(v.length > MAX_LEN ? v.slice(0, MAX_LEN) : v);
            setSaveSuccess(false);
            setNameError('');
          }}
          onBlur={() => {
            if (!savingName) submitName();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitName();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelEditing();
            }
          }}
          InputLabelProps={{
            sx: {
              fontWeight: 700,
            },
          }}
        />
      )}

      {/* Custom helper row (always visible, not tied to MUI helperText) */}
      <Box
        sx={{
          mt: 0.5,
          minHeight: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            pr: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: nameError ? 'error.main' : saveSuccess ? 'success.main' : 'transparent',
          }}
        >
          {message || '—'}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {counter}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProjectDetailsNameCard;
